import { ReactNode } from "react";

import styled, { createGlobalStyle } from "styled-components";

import { DrawerContextProvider } from "./DrawerContext";
import { Content, Footer, Header, PageWrapper, Wrapper } from "./components";

export function Drawer({
  children,
  className = "",
  position = "right",
  isOpen = false,
  isOverlay = false,
  testId = DRAWER_TEST_ID.DRAWER,
  width = 320,
  onClose = (): void => {},
}: DrawerProps) {
  return (
    <DrawerContextProvider value={{ onClose }}>
      <Container
        data-testid={testId}
        className={`${className} drawer ${position} ${isOpen ? "open" : ""} ${
          isOverlay ? "overlay" : ""
        }`}
        $width={width}
      >
        {children}
      </Container>
      {isOpen && position === "right" && <GlobalStyle $width={width} />}
    </DrawerContextProvider>
  );
}

Drawer.Header = Header;
Drawer.Footer = Footer;
Drawer.Content = Content;
Drawer.Wrapper = Wrapper;
Drawer.PageWrapper = PageWrapper;

const DRAWER_TEST_ID = {
  DRAWER: "drawer-container",
} as const;

export type DrawerProps = {
  children?: ReactNode;
  className?: string;
  position?: "left" | "right";
  isOpen?: boolean;
  isOverlay?: boolean;
  testId?: string;
  width?: number;
  onClose?: () => void;
};

const GlobalStyle = createGlobalStyle<{ $width: number }>`
  body {
    .zendesk-messaging iframe {
      margin-right: 16px !important;
      transition: right 320ms cubic-bezier(0.21, 0.92, 0.74, 0.88);
      right: ${({ $width }) => $width}px !important;
    }
  }
`;

const Container = styled.div<{ $width: number }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: ${({ $width }) => $width}px;
  background-color: var(--color-secondary-100);
  transition: margin 320ms cubic-bezier(0.21, 0.92, 0.74, 0.88);
  top: 0;
  bottom: 0;

  &.left {
    margin-left: -${({ $width }) => $width}px;
    left: 0;
  }

  &.right {
    order: 2;
    margin-right: -${({ $width }) => $width}px;
    right: 0;
  }

  &.open {
    margin: 0;
    box-shadow: var(--elevation-2);
  }

  &.overlay {
    position: absolute;
  }

  & > * {
    box-sizing: border-box;
  }

  .body {
    padding: 20px 16px 16px;
  }
`;
